(() => {
  if (window.dmrHomeInitialized) return;
  window.dmrHomeInitialized = true;
  const visibility = new Map();
  const manualPauses = new WeakSet();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeDialog = null;
  let busy = false;
  const loadVideo = video => {
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.load();
    }
  };
  const syncToggle = video => {
    const control = video.closest('.dmr-home-hero-film')?.querySelector('[data-dmr-toggle]');
    if (!control) return;
    control.setAttribute('aria-label', video.paused ? control.dataset.playLabel : control.dataset.pauseLabel);
    control.setAttribute('aria-pressed', String(!video.paused));
    control.querySelector('span').textContent = video.paused ? '▷' : 'Ⅱ';
  };
  const autoPlay = async () => {
    if (busy) return;
    const suppressed = document.hidden || activeDialog?.open || reduced.matches || navigator.connection?.saveData;
    const candidate = suppressed ? null : [...visibility].filter(([v, r]) => v.isConnected && r >= .45 && !manualPauses.has(v)).sort((a, b) => b[1] - a[1])[0]?.[0];
    visibility.forEach((ratio, video) => {
      if (video !== candidate && !video.paused) video.pause();
    });
    if (candidate && candidate.paused) {
      busy = true;
      loadVideo(candidate);
      candidate.muted = true;
      try { await candidate.play(); } catch (_) {}
      busy = false;
    }
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => visibility.set(entry.target, entry.intersectionRatio));
    autoPlay();
  }, {threshold: [0, .2, .45, .65, .9, 1]});
  const initialise = scope => {
    scope.querySelectorAll('.dmr-home [data-dmr-auto]').forEach(video => {
      if (video.dataset.dmrReady) return;
      video.dataset.dmrReady = 'true';
      visibility.set(video, 0);
      observer.observe(video);
      ['play', 'pause', 'ended'].forEach(event => video.addEventListener(event, () => syncToggle(video)));
    });
    scope.querySelectorAll('[data-dmr-film-dialog]').forEach(dialog => {
      if (dialog.dataset.dmrReady) return;
      dialog.dataset.dmrReady = 'true';
      dialog.addEventListener('close', () => {
        const video = dialog.querySelector('video');
        video.pause();
        video.removeAttribute('src');
        video.load();
        if (activeDialog === dialog) activeDialog = null;
        autoPlay();
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
    });
  };
  document.addEventListener('play', event => {
    if (!(event.target instanceof HTMLVideoElement) || !event.target.closest('.dmr-home')) return;
    document.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
  }, true);
  document.addEventListener('click', event => {
    const target = event.target.closest('button');
    if (!target || !target.closest('.dmr-home')) return;
    if (target.matches('[data-dmr-toggle]')) {
      const video = target.closest('.dmr-home-hero-film').querySelector('video');
      if (video.paused) {
        manualPauses.delete(video);
        loadVideo(video);
        video.muted = true;
        video.play().catch(() => {});
      } else {
        manualPauses.add(video);
        video.pause();
      }
    }
    if (target.matches('[data-dmr-tab]')) {
      const root = target.closest('.dmr-home-finishes');
      root.querySelectorAll('[data-dmr-tab]').forEach(tab => {
        const selected = tab === target;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        document.getElementById(tab.getAttribute('aria-controls')).hidden = !selected;
      });
      root.querySelectorAll('[data-dmr-tab-copy]').forEach(copy => { copy.hidden = copy.dataset.dmrTabCopy !== target.dataset.dmrTab; });
    }
    if (target.matches('[data-dmr-film]')) {
      const dialog = target.closest('.dmr-home-finishes').querySelector('[data-dmr-film-dialog]');
      if (activeDialog?.open) activeDialog.close();
      activeDialog = dialog;
      const video = dialog.querySelector('video');
      video.src = target.dataset.dmrFilm;
      video.muted = true;
      video.setAttribute('aria-label', target.dataset.dmrFilmLabel);
      dialog.querySelector('[data-dmr-film-caption]').textContent = target.dataset.dmrFilmLabel;
      dialog.showModal();
      video.play().catch(() => {});
    }
    if (target.matches('[data-dmr-film-close]')) target.closest('dialog').close();
  });
  document.addEventListener('keydown', event => {
    if (!event.target.matches('.dmr-home [data-dmr-tab]')) return;
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const tabs = [...event.target.parentElement.querySelectorAll('[data-dmr-tab]')];
    const index = tabs.indexOf(event.target);
    let next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    event.preventDefault();
    tabs[next].focus();
    tabs[next].click();
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('.dmr-home [data-dmr-compare]')) return;
    event.target.parentElement.style.setProperty('--position', `${event.target.value}%`);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('.dmr-home video').forEach(video => video.pause());
    else autoPlay();
  });
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-dmr-auto]').forEach(video => { observer.unobserve(video); visibility.delete(video); video.pause(); });
    event.target.querySelectorAll('[data-dmr-film-dialog]').forEach(dialog => { if (dialog.open) dialog.close(); });
  });
  document.addEventListener('shopify:block:select', event => {
    if (!event.target.matches('.dmr-home-finish-visual')) return;
    const button = document.querySelector(`[aria-controls="${event.target.id}"]`);
    button?.click();
  });
  reduced.addEventListener('change', autoPlay);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(document), {once: true});
  else initialise(document);
})();
